import { isValidElement, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import type { ComponentPropsWithoutRef, ReactNode } from 'react'

function toText(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(toText).join('')
  if (isValidElement<{ children?: ReactNode }>(node))
    return toText(node.props.children)
  return ''
}

/**
 * Fenced code in a blog post: language label + copy button in a header bar,
 * the same chrome as the home page's CodeSample. Receives the `pre` props
 * from react-markdown; the child is the `<code class="language-x">`.
 */
export function BlogCodeBlock({
  children,
  ...props
}: ComponentPropsWithoutRef<'pre'>) {
  const [copied, setCopied] = useState(false)

  const className = isValidElement<{ className?: string }>(children)
    ? (children.props.className ?? '')
    : ''
  const language = /language-([\w-]+)/.exec(className)?.[1] ?? 'code'

  const handleCopy = async () => {
    await navigator.clipboard.writeText(toText(children).trimEnd())
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="blog-code-block overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-primary)]">
      <div className="flex items-center justify-between gap-3 border-b border-[var(--color-border)] bg-[var(--color-bg-secondary)]/60 py-1.5 pr-1.5 pl-4">
        <span className="font-mono text-caption text-[var(--color-text-tertiary)]">
          {language}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 font-mono text-caption text-[var(--color-text-tertiary)] transition-all duration-300 ease-brisk hover:bg-[var(--color-control-surface-hover)] hover:text-[var(--color-text-primary)] active:scale-[0.97] focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-primary/50"
          aria-label="Copy code"
        >
          {copied ? (
            <>
              <Check className="h-3 w-3 text-[var(--color-success-text)]" />
              copied
            </>
          ) : (
            <>
              <Copy className="h-3 w-3" />
              copy
            </>
          )}
        </button>
      </div>
      <pre {...props}>{children}</pre>
    </div>
  )
}
